import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  alpha,
} from '@mui/material';
import {
  RefreshOutlined,
  PlayArrowOutlined,
  SpeedOutlined,
  RestartAltOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { formatDateTime } from '../utils/datetime';

interface LoadTestResult {
  max_parallel_requests?: number;
  successful_requests?: number;
  failed_requests?: number;
  avg_response_time?: number;
  timestamp?: string;
  detail?: string;
}

const MetricItem: React.FC<{ label: string; value: string | number | undefined }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: '0.67rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const OperationsPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({
    open: false, msg: '', severity: 'success',
  });
  const [loadStats, setLoadStats] = useState<LoadTestResult | null>(null);

  useEffect(() => { fetchLoadStats(); }, []);

  const showSnack = (msg: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnack({ open: true, msg, severity });
  };

  const fetchLoadStats = async () => {
    try {
      const stats = await apiService.getLoadStats();
      setLoadStats(stats);
    } catch {}
  };

  const handleResetTokens = async () => {
    if (!window.confirm('Reset token usage counters for all applications? This cannot be undone.')) return;
    setLoading('reset');
    try {
      const result = await apiService.resetTokens();
      showSnack(result.message || 'Token usage reset successfully');
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to reset tokens', 'error');
    } finally {
      setLoading(null);
    }
  };

  const handleRunLoadTest = async () => {
    if (!window.confirm('Run a load test against the proxy service?')) return;
    setLoading('loadtest');
    try {
      const result = await apiService.runLoadTest();
      if (result.status === 'already_running') {
        showSnack(result.message, 'info');
      } else {
        showSnack('Load test started. Refresh stats to see results.');
      }
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to start load test', 'error');
    } finally {
      setLoading(null);
    }
  };

  const handleRefreshStats = async () => {
    setLoading('refresh');
    try {
      await fetchLoadStats();
      showSnack('Stats refreshed', 'info');
    } catch {
      showSnack('Failed to refresh stats', 'error');
    } finally {
      setLoading(null);
    }
  };

  const canResetTokens = user?.permissions?.can_reset_tokens;
  const canRunLoadTests = user?.permissions?.can_run_load_tests;

  const sectionBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)';

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: 'text.primary', letterSpacing: '-0.02em' }}>
          Operations
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
          System management and diagnostics
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
        {/* Token Management */}
        <Paper elevation={1} sx={{ flex: '1 1 280px', minWidth: 280, borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{
            px: 3, py: 2.5,
            borderBottom: sectionBorder,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              width: 38, height: 38,
              bgcolor: isDark ? alpha('#F59E0B', 0.12) : 'rgba(245,158,11,0.1)',
              border: isDark ? `1px solid ${alpha('#F59E0B', 0.25)}` : '1px solid rgba(245,158,11,0.2)',
              borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#F59E0B',
            }}>
              <RestartAltOutlined fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', letterSpacing: '-0.01em' }}>
                Token Management
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Reset daily usage counters
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
              Reset token usage for all applications. Daily quotas will restart from zero.
            </Typography>
            <Button
              variant="contained"
              onClick={handleResetTokens}
              disabled={!canResetTokens || loading === 'reset'}
              startIcon={loading === 'reset' ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlined />}
              sx={{
                bgcolor: '#F59E0B', color: '#000',
                '&:hover': { bgcolor: '#D97706' },
                '&:disabled': { bgcolor: isDark ? alpha('#F59E0B', 0.2) : '#FDE68A', color: isDark ? '#64748B' : '#92400E' },
                textTransform: 'none',
              }}
            >
              Reset Token Usage
            </Button>
            {!canResetTokens && (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 1 }}>
                Requires Admin or Super Admin role
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Load Testing */}
        <Paper elevation={1} sx={{ flex: '2 1 400px', minWidth: 320, borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{
            px: 3, py: 2.5,
            borderBottom: sectionBorder,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              width: 38, height: 38,
              bgcolor: isDark ? alpha('#3B82F6', 0.12) : 'rgba(59,130,246,0.1)',
              border: isDark ? `1px solid ${alpha('#3B82F6', 0.25)}` : '1px solid rgba(59,130,246,0.2)',
              borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#3B82F6',
            }}>
              <SpeedOutlined fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', letterSpacing: '-0.01em' }}>
                Load Testing
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Proxy performance benchmarks
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
              Run concurrent request tests against the proxy to measure throughput and latency.
            </Typography>

            {loadStats && !loadStats.detail && (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 2,
                p: 2.5,
                mb: 2.5,
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC',
                borderRadius: '12px',
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #E2E8F0',
              }}>
                <MetricItem label="Max Parallel" value={loadStats.max_parallel_requests} />
                <MetricItem label="Successful" value={loadStats.successful_requests} />
                <MetricItem label="Failed" value={loadStats.failed_requests} />
                <MetricItem
                  label="Avg Response"
                  value={loadStats.avg_response_time ? `${loadStats.avg_response_time.toFixed(0)}ms` : undefined}
                />
              </Box>
            )}
            {loadStats?.detail && (
              <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.8rem' }}>{loadStats.detail}</Alert>
            )}
            {loadStats?.timestamp && !loadStats.detail && (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 2 }}>
                Last run: {formatDateTime(loadStats.timestamp, user?.timezone)}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleRunLoadTest}
                disabled={!canRunLoadTests || loading === 'loadtest'}
                startIcon={loading === 'loadtest' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlined />}
                sx={{ textTransform: 'none' }}
              >
                Run Test
              </Button>
              <Button
                variant="outlined"
                onClick={handleRefreshStats}
                disabled={loading === 'refresh'}
                startIcon={loading === 'refresh' ? <CircularProgress size={16} /> : <RefreshOutlined />}
                sx={{ textTransform: 'none' }}
              >
                Refresh Stats
              </Button>
            </Box>
            {!canRunLoadTests && (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 1 }}>
                Requires Admin or Super Admin role
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OperationsPage;
